/**
 * SchematicGenerator/DiffTable.jsx — v2.48/v2.49/v2.50 side-by-side CSV diff
 * with inline cell editing and CSV export.
 *
 * Extracted from SchematicGeneratorPanel.jsx during the v2.55 P3
 * refactor. Rows = tokens, columns = drawings. Cells are colour-coded:
 *   • Empty       → red    (missing override / cell blank)
 *   • Duplicated  → amber  (same value on ≥2 rows)
 *   • Unique      → default gray
 * The currently-previewed drawing's column is highlighted cyan.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple } from "@phosphor-icons/react";

export function StatCard({ label, value, ...rest }) {
  return (
    <div className="border border-white/10 bg-[#0F1218] p-2" {...rest}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-lg font-bold text-white font-mono">{value}</div>
    </div>
  );
}

export function DiffTable({ columns, rows, currentDwg, sessionId, onCellUpdated }) {
  const dupMap = {};
  for (const col of columns) {
    const counts = {};
    for (const r of rows) {
      const v = (r.values?.[col] ?? "").trim();
      if (!v) continue;
      counts[v] = (counts[v] || 0) + 1;
    }
    dupMap[col] = counts;
  }

  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const startEdit = (dwg, col, currentVal) => {
    setEditKey(`${dwg}::${col}`);
    setDraft(currentVal || "");
  };
  const cancelEdit = () => { setEditKey(null); setDraft(""); };
  const commitEdit = async (dwg, col) => {
    setSaving(true);
    try {
      await api.patch(`/schematic-gen/sessions/${sessionId}/csv-cell`,
                        null,
                        { params: { drawing_no: dwg, column: col,
                                       value: draft } });
      cancelEdit();
      if (onCellUpdated) await onCellUpdated();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const onExportDiffCsv = () => {
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["TOKEN", ...rows.map((r) =>
      r.drawing_no + (r.drawing_no === currentDwg ? " [preview]" : "")
                    + (r.matched ? "" : " [no-template]"))];
    const lines = [header.map(escape).join(",")];
    for (const col of columns) {
      const cells = rows.map((r) => {
        const val = (r.values?.[col] ?? "");
        const trimmed = val.trim();
        if (!trimmed) return "(empty) [empty]";
        if (dupMap[col][trimmed] > 1) return `${val} [dup×${dupMap[col][trimmed]}]`;
        return val;
      });
      lines.push([col, ...cells].map(escape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `diff_${currentDwg || "preview"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="overflow-x-auto max-h-64" data-testid="schgen-diff-table">
      <table className="w-full text-[11px] font-mono border-collapse">
        <thead className="sticky top-0 bg-[#12151C]">
          <tr>
            <th className="text-left px-2 py-1.5 text-gray-500 uppercase text-[9px] tracking-wider border-b border-white/10">
              Token
            </th>
            {rows.map((r) => (
              <th key={r.drawing_no}
                  className={`text-left px-2 py-1.5 border-b border-white/10 whitespace-nowrap ${r.drawing_no === currentDwg ? "text-[#00E5FF] bg-[#00E5FF]/5" : "text-gray-300"}`}
                  data-testid={`schgen-diff-col-${r.drawing_no}`}>
                {r.drawing_no}
                {!r.matched && (
                  <span title="No matching template — this row will be skipped"
                        className="ml-1 text-[9px] text-[#FFCC00]">·no template</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {columns.map((col) => (
            <tr key={col} className="border-b border-white/5">
              <td className="px-2 py-1 text-gray-400 whitespace-nowrap">{col}</td>
              {rows.map((r) => {
                const val = (r.values?.[col] ?? "");
                const trimmed = val.trim();
                const isEmpty = trimmed.length === 0;
                const isDup   = !isEmpty && dupMap[col][trimmed] > 1;
                const cellTone = isEmpty ? "border-[#FF6B6B]/60 bg-[#FF6B6B]/10 text-[#FF6B6B]"
                                  : isDup  ? "border-[#FFCC00]/40 bg-[#FFCC00]/5 text-[#FFCC00]"
                                            : "border-transparent text-gray-200";
                const currentCol = r.drawing_no === currentDwg;
                const cellKey = `${r.drawing_no}::${col}`;
                const editing = editKey === cellKey;
                if (editing) {
                  return (
                    <td key={r.drawing_no}
                        className={`px-1 py-0.5 border border-[#00E5FF] bg-[#00E5FF]/10 ${currentCol ? "bg-[#00E5FF]/15" : ""}`}
                        data-testid={`schgen-diff-cell-${r.drawing_no}-${col}`}
                        data-editing="true">
                      <input value={draft}
                              autoFocus
                              disabled={saving}
                              onChange={(e) => setDraft(e.target.value)}
                              onBlur={() => commitEdit(r.drawing_no, col)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitEdit(r.drawing_no, col);
                                else if (e.key === "Escape") cancelEdit();
                              }}
                              className="w-full bg-transparent border-0 outline-none text-white font-mono text-[11px] px-1"
                              data-testid={`schgen-diff-cell-input-${r.drawing_no}-${col}`} />
                    </td>
                  );
                }
                return (
                  <td key={r.drawing_no}
                      className={`px-2 py-1 border ${cellTone} ${currentCol ? "bg-[#00E5FF]/5" : ""} cursor-text hover:ring-1 hover:ring-[#00E5FF]/40`}
                      data-testid={`schgen-diff-cell-${r.drawing_no}-${col}`}
                      data-empty={isEmpty ? "true" : "false"}
                      data-duplicate={isDup ? "true" : "false"}
                      onClick={() => startEdit(r.drawing_no, col, val)}
                      title={isEmpty ? "Missing value — click to edit"
                              : isDup ? `Duplicate value across ${dupMap[col][trimmed]} rows — click to override`
                              : "Click to edit"}>
                    {isEmpty ? <span className="opacity-60">(empty)</span> : val}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-between gap-3 p-2 border-t border-white/5">
        <div className="flex items-center gap-3 text-[9px] font-mono text-gray-500">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 border border-[#00E5FF] bg-[#00E5FF]/20" /> Previewed</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 border border-[#FFCC00] bg-[#FFCC00]/20" /> Duplicate</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 border border-[#FF6B6B] bg-[#FF6B6B]/20" /> Empty</span>
        </div>
        <button onClick={onExportDiffCsv}
                className="inline-flex items-center gap-1.5 text-[10px] font-mono px-2 py-1 border border-white/15 text-gray-300 hover:border-[#00E5FF] hover:text-[#00E5FF]"
                data-testid="schgen-diff-export-btn"
                title="Download the diff view as a CSV with [preview]/[dup×N]/[empty] flags">
          <DownloadSimple size={11} /> Export diff CSV
        </button>
      </div>
    </div>
  );
}
